import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Radio from "antd/es/radio";
import Space from "antd/es/space";
import { defineMessages, useIntl } from "react-intl";

import BaseMapStyle from "@mapmycustomers/shared/enum/map/BaseMapStyle";

import RadioGroup from "@app/component/input/RadioGroup";
import AlignedModal from "@app/component/modal/AlignedModal";
import Placement from "@app/enum/Placement";
import { updateMapStyle } from "@app/scene/map/store/actions";
import { getMapStyle } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getBaseMapStyleDisplayName } from "@app/util/ui";

const messages = defineMessages({
  selectBasemapModal: {
    id: "map.toolbar.toolSelection.selectBasemap.modal",
    defaultMessage: "Select Basemap",
    description: "Select Basemap modal title",
  },
});

interface Props {
  baseMapButtonRef?: HTMLElement | null;
  mapStyle: BaseMapStyle;
  onChange: (mapStyle: BaseMapStyle) => void;
  onHide: () => void;
  visible: boolean;
}

const BaseMapStyleModal: React.FC<Props> = ({
  baseMapButtonRef,
  mapStyle,
  onChange,
  onHide,
  visible,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.initiated(["Basemap"]);
    return () => {
      analytics.completed(["Basemap"]);
    };
  }, [analytics]);

  const handleChange = useCallback(
    (mapStyle: BaseMapStyle) => {
      onChange(mapStyle);
      analytics.clicked(["Basemap", getBaseMapStyleDisplayName(intl, mapStyle)]);
    },
    [analytics, intl, onChange]
  );

  return (
    <AlignedModal
      destroyOnClose
      element={baseMapButtonRef}
      footer={null}
      mask={false}
      onCancel={onHide}
      placement={Placement.LEFT_BOTTOM}
      title={intl.formatMessage(messages.selectBasemapModal)}
      visible={visible}
      width={212}
    >
      <RadioGroup<BaseMapStyle> onChange={handleChange} value={mapStyle}>
        <Space direction="vertical">
          {[
            BaseMapStyle.MUTED,
            BaseMapStyle.STANDARD,
            BaseMapStyle.NIGHT,
            BaseMapStyle.SUBTLE_GRAY,
            BaseMapStyle.MIDNIGHT,
            BaseMapStyle.HYBRID,
            BaseMapStyle.SATELLITE,
          ].map((style) => (
            <Radio key={style} value={style}>
              {getBaseMapStyleDisplayName(intl, style)}
            </Radio>
          ))}
        </Space>
      </RadioGroup>
    </AlignedModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  mapStyle: getMapStyle(state),
});

const mapDispatchToProps = {
  onChange: updateMapStyle,
};

export default connect(mapStateToProps, mapDispatchToProps)(BaseMapStyleModal);
